import merge from 'lodash/merge';
import { useState } from 'react';
// @mui
import { Box, Card, CardHeader, TextField } from '@mui/material';
// components
import ReactApexChart, { BaseOptionChart } from 'src/components/chart';
import { LocationSummaryGraph } from 'src/hooks/core-api/useCallStatistics';

// ----------------------------------------------------------------------

interface DashboardSummaryGraphProps {
  data: LocationSummaryGraph;
  onFilterChange?: (newFilter: string) => void;
}

interface ChartData {
  name: string;
  data: number[];
}

interface Chart {
  category: string;
  description: string;
  labels: string[];
  data: ChartData[];
}

const DashboardSummaryGraph: React.FC<DashboardSummaryGraphProps> = ({ data, onFilterChange }) => {
  const { total_calls, texts_sent, connected_to_shop } = data;

  const CHART_DATA: Chart[] = [
    {
      category: 'Calls Answered by Lovage',
      description: 'Total amount of inbound calls that Lovage has answered for you',
      labels: total_calls.labels,
      data: [
        {
          name: 'Calls Answered by Lovage',
          data: total_calls.values,
        },
      ],
    },
    {
      category: 'Calls Rerouted to Shop',
      description: 'Total amount of calls that were rerouted to an employee',
      labels: connected_to_shop.labels,
      data: [
        {
          name: 'Calls Rerouted to Shop',
          data: connected_to_shop.values,
        },
      ],
    },
    {
      category: 'Texts Sent',
      description: 'Total count of texts sent to customers by Lovage',
      labels: texts_sent.labels,

      data: [
        {
          name: 'Texts Sent',
          data: texts_sent.values,
        },
      ],
    },
    // {
    //   category: 'Links Clicked',
    //   description: 'Total text message links clicked by your customers',
    //   labels: links_clicked.labels,

    //   data: [
    //     {
    //       name: 'Links Clicked',
    //       data: links_clicked.values,
    //     },
    //   ],
    // },
  ];

  const [selectedSeries, setSelectedSeries] = useState(CHART_DATA[0]);

  const handleChangeSeriesData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedSeriesCategory = event.target.value;

    const selectedSeries = CHART_DATA.find((x) => x.category === selectedSeriesCategory);

    if (!selectedSeries) {
      throw new Error(`Could not find series for category: ${selectedSeriesCategory}`);
    }

    if (onFilterChange) {
      onFilterChange(selectedSeriesCategory);
    }

    setSelectedSeries(selectedSeries);
  };

  const chartOptions = merge(BaseOptionChart(), {
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: selectedSeries.labels,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val}`,
      },
    },
  });

  return (
    <Card>
      <CardHeader
        title="Call Statistics"
        subheader={selectedSeries.description}
        action={
          <TextField
            select
            fullWidth
            value={selectedSeries.category}
            SelectProps={{ native: true }}
            onChange={handleChangeSeriesData}
            sx={{
              '& fieldset': { border: '0 !important' },
              '& select': { pl: 1, py: 0.5, pr: '24px !important', typography: 'subtitle2' },
              '& .MuiOutlinedInput-root': { borderRadius: 0.75, bgcolor: 'background.neutral' },
              '& .MuiNativeSelect-icon': { top: 4, right: 0, width: 20, height: 20 },
            }}
          >
            {CHART_DATA.map((option) => (
              <option key={option.category} value={option.category}>
                {option.category}
              </option>
            ))}
          </TextField>
        }
      />

      {CHART_DATA.map((item) => (
        <Box key={item.category} sx={{ mt: 3, mx: 3 }} dir="ltr">
          {item.category === selectedSeries.category && (
            <ReactApexChart type="bar" series={item.data} options={chartOptions} height={364} />
          )}
        </Box>
      ))}
    </Card>
  );
};

export default DashboardSummaryGraph;
